import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Divider,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Snackbar,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

interface Member {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  last_login: string | null;
  date_joined: string;
}

interface TeamInvitation {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: 'pending' | 'expired' | 'accepted';
  created_at: string;
  expires_at: string;
  invited_by_email: string | null;
  email_sent_at: string | null;
  accept_url: string | null;
}

function ProfileSection() {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState(user?.first_name ?? '');
  const [lastName, setLastName] = useState(user?.last_name ?? '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState({ text: '', severity: 'success' as 'success' | 'error' });

  const handleProfileSave = async () => {
    setProfileSaving(true);
    setProfileMsg('');
    try {
      await api.put('/auth/me/', { first_name: firstName, last_name: lastName });
      setProfileMsg('Profile updated.');
    } catch {
      setProfileMsg('Failed to update profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setPwMsg({ text: 'Passwords do not match.', severity: 'error' });
      return;
    }
    setPwSaving(true);
    setPwMsg({ text: '', severity: 'success' });
    try {
      await api.post('/auth/change-password/', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPwMsg({ text: 'Password changed.', severity: 'success' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPwMsg({ text: err.response?.data?.error || 'Failed to change password.', severity: 'error' });
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Profile</Typography>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 4 }}>
          {/* Left: name & email */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField label="First name" size="small" fullWidth
                value={firstName} onChange={e => setFirstName(e.target.value)} />
              <TextField label="Last name" size="small" fullWidth
                value={lastName} onChange={e => setLastName(e.target.value)} />
            </Box>
            <TextField label="Email" size="small" fullWidth disabled value={user?.email ?? ''} sx={{ mb: 2 }} />
            {profileMsg && <Alert severity="success" sx={{ mb: 2 }}>{profileMsg}</Alert>}
            <Button variant="contained" size="small" onClick={handleProfileSave} disabled={profileSaving}
              sx={{ textTransform: 'none', bgcolor: '#10A4C2', '&:hover': { bgcolor: '#0D8DA7' } }}>
              {profileSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Right: password */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Change password</Typography>
            <TextField label="Current password" type="password" size="small" fullWidth
              value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField label="New password" type="password" size="small" fullWidth
                value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              <TextField label="Confirm" type="password" size="small" fullWidth
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
            </Box>
            {pwMsg.text && <Alert severity={pwMsg.severity} sx={{ mb: 2 }}>{pwMsg.text}</Alert>}
            <Button variant="contained" size="small" onClick={handlePasswordChange}
              disabled={pwSaving || !currentPassword || newPassword.length < 8 || !confirmPassword}
              sx={{ textTransform: 'none', bgcolor: '#10A4C2', '&:hover': { bgcolor: '#0D8DA7' } }}>
              {pwSaving ? 'Changing...' : 'Change password'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default function SettingsPage() {
  const { currentAccount } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [seats, setSeats] = useState<{ max_size: number; seats_used: number; seats_remaining: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', first_name: '', last_name: '' });
  const [inviteError, setInviteError] = useState('');
  const [inviteSaving, setInviteSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/account/members/'),
      api.get('/account/invitations/'),
    ]).then(([mRes, iRes]) => {
      setMembers(mRes.data.members);
      setSeats({
        max_size: mRes.data.max_size,
        seats_used: mRes.data.seats_used,
        seats_remaining: mRes.data.seats_remaining,
      });
      setInvitations(iRes.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load, currentAccount]);

  const formatDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const handleInvite = async (sendEmail: boolean) => {
    setInviteError('');
    setInviteSaving(true);
    try {
      await api.post('/account/invitations/', { ...inviteForm, send_email: sendEmail });
      setInviteOpen(false);
      setInviteForm({ email: '', first_name: '', last_name: '' });
      setSnackbar({
        open: true,
        message: sendEmail
          ? 'User added and invite email sent.'
          : 'User added — send the invite email or copy their invite link when ready.',
        severity: 'success',
      });
      load();
    } catch (err: any) {
      setInviteError(err.response?.data?.error || err.response?.data?.email?.[0] || 'Failed to add user.');
    } finally {
      setInviteSaving(false);
    }
  };

  const handleCopyLink = async (inv: TeamInvitation) => {
    if (!inv.accept_url) return;
    try {
      await navigator.clipboard.writeText(inv.accept_url);
      setSnackbar({ open: true, message: 'Invite link copied to clipboard.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Could not copy — your browser blocked clipboard access.', severity: 'error' });
    }
  };

  const handleResend = async (id: string) => {
    try {
      await api.post(`/account/invitations/${id}/resend/`);
      setSnackbar({ open: true, message: 'Invite email sent.', severity: 'success' });
      load();
    } catch {
      setSnackbar({ open: true, message: 'Failed to send invite email.', severity: 'error' });
    }
  };

  const handleDeleteInvite = async (id: string) => {
    try {
      await api.delete(`/account/invitations/${id}/delete/`);
      setSnackbar({ open: true, message: 'Invitation cancelled.', severity: 'success' });
      load();
    } catch {
      setSnackbar({ open: true, message: 'Failed to cancel.', severity: 'error' });
    }
  };

  const pendingInvitations = invitations.filter(i => i.status !== 'accepted');
  // Members have no row actions today — only show the column when invited
  // users (copy link / send email / remove) give it something to hold.
  const showActions = pendingInvitations.length > 0;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Settings</Typography>

      <ProfileSection />

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography variant="h6" fontWeight={600}>Team</Typography>
          {seats && (
            <Typography variant="caption" sx={{ color: seats.seats_remaining === 0 ? '#DC2626' : '#6B7280' }}>
              {seats.seats_used} of {seats.max_size} seats used
              {seats.seats_remaining > 0
                ? ` · ${seats.seats_remaining} remaining`
                : ' · no seats remaining'}
            </Typography>
          )}
        </Box>
        <Button size="small" variant="contained" startIcon={<PersonAddOutlinedIcon />}
          onClick={() => setInviteOpen(true)}
          disabled={seats?.seats_remaining === 0}
          sx={{ textTransform: 'none', bgcolor: '#10A4C2', '&:hover': { bgcolor: '#0D8DA7' } }}>
          Add user
        </Button>
      </Box>

      {/* Team: members + invited users in one table */}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
              {showActions && <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map(m => (
              <TableRow key={m.id}>
                <TableCell>{`${m.first_name} ${m.last_name}`.trim() || '—'}</TableCell>
                <TableCell>{m.email}</TableCell>
                <TableCell>
                  <Chip label={m.is_active ? 'Active' : 'Inactive'} size="small"
                    color={m.is_active ? 'success' : 'default'} variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                </TableCell>
                <TableCell>{formatDate(m.date_joined)}</TableCell>
                {showActions && <TableCell align="right" />}
              </TableRow>
            ))}
            {pendingInvitations.map(inv => (
              <TableRow key={inv.id}>
                <TableCell>{`${inv.first_name} ${inv.last_name}`.trim() || '—'}</TableCell>
                <TableCell>{inv.email}</TableCell>
                <TableCell>
                  <Tooltip title={inv.email_sent_at
                    ? `Invite email sent ${formatDate(inv.email_sent_at)} · expires ${formatDate(inv.expires_at)}`
                    : `Added ${formatDate(inv.created_at)} — invite email not sent yet`}>
                    <Chip label={inv.status === 'expired' ? 'Expired' : 'Invited'} size="small"
                      color={inv.status === 'expired' ? 'warning' : 'info'} variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }} />
                  </Tooltip>
                </TableCell>
                <TableCell>—</TableCell>
                <TableCell align="right">
                  <Tooltip title="Copy invite link">
                    <IconButton size="small" onClick={() => handleCopyLink(inv)}><ContentCopyIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title={inv.email_sent_at ? 'Resend invite email' : 'Send invite email'}>
                    <IconButton size="small" onClick={() => handleResend(inv.id)}><SendIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => handleDeleteInvite(inv.id)} sx={{ color: '#EF4444' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add user dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add team member</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {inviteError && <Alert severity="error" sx={{ mb: 1 }}>{inviteError}</Alert>}
          <TextField label="Email" type="email" required fullWidth size="small"
            value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="First name" size="small" fullWidth
              value={inviteForm.first_name} onChange={e => setInviteForm({ ...inviteForm, first_name: e.target.value })} />
            <TextField label="Last name" size="small" fullWidth
              value={inviteForm.last_name} onChange={e => setInviteForm({ ...inviteForm, last_name: e.target.value })} />
          </Box>
          <Typography variant="caption" sx={{ color: '#6B7280' }}>
            "Add" creates the user without emailing them — send the invite email or copy
            their invite link from the team list whenever you're ready.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInviteOpen(false)} sx={{ textTransform: 'none', color: '#6B7280' }}>Cancel</Button>
          <Button variant="outlined" onClick={() => handleInvite(false)} disabled={!inviteForm.email || inviteSaving}
            sx={{ textTransform: 'none', color: '#10A4C2', borderColor: '#10A4C2' }}>
            {inviteSaving ? 'Adding...' : 'Add'}
          </Button>
          <Button variant="contained" onClick={() => handleInvite(true)} disabled={!inviteForm.email || inviteSaving}
            sx={{ textTransform: 'none', bgcolor: '#10A4C2', '&:hover': { bgcolor: '#0D8DA7' } }}>
            {inviteSaving ? 'Adding...' : 'Add & send invite'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
